import { PageSeo } from '../services/seo.model';
import {
  CHAINMART_HOME_SEO_TEXT,
  SEO_DEFAULT_DOCUMENT_TITLE,
  formatSeoTitle,
} from '../services/seo.defaults';

const seo = (partial: Partial<PageSeo>): { seo: Partial<PageSeo> } => ({ seo: partial });

/** Reusable `data: …` for `app.routes` — merged with `DEFAULT_PAGE_SEO` in `SeoService`. */
export const routeSeo = {
  home: seo({
    title: SEO_DEFAULT_DOCUMENT_TITLE,
    description: CHAINMART_HOME_SEO_TEXT,
    ogDescription: CHAINMART_HOME_SEO_TEXT,
    ogType: 'article',
    articleAuthor: 'Admin',
  }),

  orbit: seo({
    title: formatSeoTitle('Orbit Explorer'),
    description:
      'Find the Orbits that solve your business case — ranked providers across solutions on ChainMart.',
    ogDescription:
      'Search by business case, industry, or provider category. Explore ranked Orbits and shortlist verified providers.',
  }),

  orbitOpen: seo({
    title: formatSeoTitle('Open Orbit'),
    description: 'Compare ranked providers for a solution Orbit on ChainMart.',
    ogDescription: 'Open an Orbit to compare providers and build your shortlist.',
  }),

  directory: seo({
    title: formatSeoTitle('Directory'),
    description: 'Find organizations and providers in the blockchain ecosystem on ChainMart.',
    ogDescription: 'Browse the ChainMart directory of organizations and service providers.',
  }),

  articlesList: seo({
    title: formatSeoTitle('Discover'),
    description: 'Read research, articles and guides on blockchain technology, from ChainMart and partner authors.',
    ogDescription: 'Discover blockchain articles and editorial content on ChainMart.',
  }),

  articlesFiltered: seo({
    title: formatSeoTitle('Articles'),
    description: 'Browse filtered blockchain articles and learning content on ChainMart.',
    ogDescription: 'Filtered articles and learning content on ChainMart.',
  }),

  /** `discover/:id` — refined when the article payload loads. */
  article: seo({
    title: formatSeoTitle('Article'),
    description: 'Read this article on ChainMart — blockchain learning and insights.',
    ogDescription: 'Article on ChainMart.',
  }),

  dictionary: seo({
    title: formatSeoTitle('Blockchain A–Z'),
    description: 'Definitions and explanations of blockchain terms, curated on ChainMart.',
    ogDescription: 'Blockchain A–Z glossary on ChainMart.',
  }),

  community: seo({
    title: formatSeoTitle('Community'),
    description: 'Content assignments and editorial programs for creators on ChainMart.',
    ogDescription: 'Join the editorial community on ChainMart.',
  }),

  providerHub: seo({
    title: formatSeoTitle('Provider Hub'),
    description:
      'ChainMart\'s home for providers and creators — listings, Orbit positioning, resources, content assignments and dictionary sponsorship.',
    ogDescription:
      'Set up your listing, manage Orbit positions, browse provider resources and claim content assignments on ChainMart.',
  }),

  registerOrganisation: seo({
    title: formatSeoTitle('Register organization'),
    description: 'Create your organization profile on ChainMart for providers and partners.',
    ogDescription: 'Register your organization on ChainMart.',
  }),

  registerCreator: seo({
    title: formatSeoTitle('Register as creator'),
    description: 'Sign up as a content creator on ChainMart.',
    ogDescription: 'Creator registration on ChainMart.',
  }),

  shop: seo({
    title: formatSeoTitle('Shop'),
    description: 'Browse a storefront on ChainMart.',
    ogDescription: 'Storefront on ChainMart.',
  }),

  organisationStorefront: seo({
    title: formatSeoTitle('Organization store'),
    description: 'Manage stores, listings, and buyer-facing content for your organization on ChainMart.',
    ogDescription: 'Organization store on ChainMart.',
  }),

  organisation: seo({
    title: formatSeoTitle('Organization'),
    description: 'Organization profile on ChainMart — providers and partners in blockchain.',
    ogDescription: 'Organization on ChainMart.',
  }),

  organisationEdit: seo({
    title: formatSeoTitle('Edit organization'),
    description: 'Update your organization profile on ChainMart.',
  }),

  referral: seo({
    title: SEO_DEFAULT_DOCUMENT_TITLE,
    description: 'You have been referred to ChainMart — blockchain content, providers, and enterprises.',
    ogDescription: 'Join ChainMart via referral.',
  }),

  profile: seo({
    title: formatSeoTitle('Profile'),
    description: 'Your profile on ChainMart.',
  }),

  dashboard: seo({
    title: formatSeoTitle('Dashboard'),
    description: 'Your ChainMart dashboard.',
  }),

  myChainmart: seo({
    title: formatSeoTitle('My ChainMart'),
    description: 'Your personal workspace on ChainMart — workbench projects, provider store, and creator activity in one place.',
    ogDescription: 'Your context-aware ChainMart workspace for buying, selling, and creating.',
  }),

  workbenchProject: seo({
    title: formatSeoTitle('Workbench project'),
    description: 'Your buying workbench — pinned orbits, shortlisted providers, snippets, and generated documents.',
    ogDescription: 'Review and manage a ChainMart workbench buying project.',
  }),

  marketplace: seo({
    title: formatSeoTitle('Marketplace'),
    description: 'Find organizations and providers in the blockchain ecosystem on ChainMart.',
    ogDescription: 'Browse the ChainMart marketplace of organizations and service providers.',
  }),

  experts: seo({
    title: formatSeoTitle('Experts'),
    description: 'Find approved independent blockchain experts on ChainMart.',
    ogDescription: 'Search experts by name, expertise, and industry, then open a profile.',
  }),

  expertProfile: seo({
    title: formatSeoTitle('Expert'),
    description: 'An independent expert profile on ChainMart.',
    ogDescription: 'Read an approved expert profile on ChainMart.',
  }),

  contentBounties: seo({
    title: formatSeoTitle('Content assignments'),
    description: 'Content assignments on ChainMart.',
  }),

  mcpConsent: seo({
    title: formatSeoTitle('Connect Claude'),
    description: 'Allow Claude to use your ChainMart AI tools.',
  }),

  /** Authenticated or editor flows; keep generic. */
  appSignedIn: seo({
    title: SEO_DEFAULT_DOCUMENT_TITLE,
    description:
      'ChainMart brings together content creators, providers and enterprises for all things blockchain.',
    ogDescription: 'Discover blockchain content, providers and enterprise solutions on ChainMart.',
  }),

  checkout: seo({
    title: formatSeoTitle('Checkout'),
    description: 'Complete your subscription purchase on ChainMart.',
    ogDescription: 'Subscription checkout on ChainMart.',
  }),

  purchaseConfirmation: seo({
    title: formatSeoTitle('Purchase confirmed'),
    description: 'Your ChainMart purchase confirmation.',
    ogDescription: 'Purchase confirmation on ChainMart.',
  }),

  admin: seo({
    title: formatSeoTitle('Admin'),
    description: 'Administration on ChainMart.',
  }),

  legalPrivacy: seo({
    title: formatSeoTitle('Privacy Policy'),
    description: 'How ChainMart collects, uses, and protects personal information.',
    ogDescription: 'ChainMart Privacy Policy.',
  }),

  legalTerms: seo({
    title: formatSeoTitle('Terms of Service'),
    description: 'Terms that govern access to and use of ChainMart services.',
    ogDescription: 'ChainMart Terms of Service.',
  }),

  /** OAuth / redirect pages — avoid misleading previews. */
  auth: seo({
    title: formatSeoTitle('Sign in'),
    description: 'Sign in to ChainMart.',
  }),

  googleCallback: seo({
    title: formatSeoTitle('Signing in'),
    description: 'Completing sign-in to ChainMart.',
  }),
} as const;
